/**
 * Phase 8 — Vendor Performance dashboard (/procurement/vendor-performance).
 * Rating-engine view: top/bottom performers, risk lists, inactive vendors,
 * expiring compliance.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Trophy, AlertTriangle, Clock, ShieldAlert, Star, BarChart3 } from 'lucide-react';
import { apiGet } from '../../lib/api';

export default function VendorPerformance() {
  const [d, setD] = useState(null);

  useEffect(() => {
    apiGet('/vendors/performance-dashboard').then(setD).catch(() => toast.error('Failed to load dashboard'));
  }, []);

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="vendor-performance-page">
      <div>
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-display">
          <BarChart3 size={11} /> Procurement · Analytics
        </div>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1 flex items-center gap-2">
          <Trophy size={22} className="text-[#1F7A6E]" /> Vendor Performance
        </h1>
        <p className="text-sm text-slate-600 mt-0.5">
          Ratings auto-update as GRNs close and bills certify — {d.rated_vendors} of {d.total_vendors} vendors rated so far.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Trophy} title={`Top Performers (${d.top_vendors.length})`} testid="vp-top">
          <VendorTable rows={d.top_vendors} empty="No rated vendors yet — ratings build as POs close" />
        </Section>
        <Section icon={Star} title={`Bottom Performers (${d.bottom_vendors.length})`} testid="vp-bottom">
          <VendorTable rows={d.bottom_vendors} empty="No rated vendors yet" />
        </Section>
        <Section icon={AlertTriangle} title={`High Rejection > 10% (${d.high_rejection.length})`} testid="vp-rejection" amber>
          <VendorTable rows={d.high_rejection} empty="No vendors above the rejection threshold" highlight="quality_rejection_pct" />
        </Section>
        <Section icon={Clock} title={`Low On-Time < 70% (${d.low_on_time.length})`} testid="vp-ontime" amber>
          <VendorTable rows={d.low_on_time} empty="All rated vendors deliver on time" highlight="on_time_pct" />
        </Section>
        <Section icon={Clock} title={`Inactive 90+ Days (${d.inactive_90_days.length})`} testid="vp-inactive">
          <VendorTable rows={d.inactive_90_days} empty="All vendors have recent orders" slim />
        </Section>
        <Section icon={ShieldAlert} title={`Expiring Compliance (${d.expiring_compliance.length})`} testid="vp-compliance" amber>
          {d.expiring_compliance.length === 0 ? <Empty text="No compliance documents expiring" /> : (
            <div className="space-y-2">
              {d.expiring_compliance.map((v) => (
                <div key={v.id} className="text-xs border-b border-slate-100 pb-1.5">
                  <span className="font-mono text-slate-500">{v.vendor_code}</span>{' '}
                  <span className="font-display font-bold">{v.name}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {v.flagged_docs.map((doc, i) => (
                      <span key={i} className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display ${doc.status === 'expired' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`}>
                        {doc.doc_type.replace(/_/g, ' ')} · {doc.expiry_date}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      <div className="text-xs text-slate-500">
        Manage vendors in the <Link to="/app/procurement/vendors" className="text-[#1F7A6E] font-bold underline">Vendor Master</Link>.
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children, testid, amber }) => (
  <div className={`bg-white border rounded-sm p-4 ${amber ? 'border-amber-200' : 'border-slate-200'}`} data-testid={testid}>
    <div className="kpi-overline flex items-center gap-1.5 mb-3"><Icon size={11} /> {title}</div>
    {children}
  </div>
);

const Empty = ({ text }) => <div className="text-xs text-slate-400 py-4 text-center">{text}</div>;

function VendorTable({ rows, empty, highlight, slim }) {
  if (!rows.length) return <Empty text={empty} />;
  return (
    <table className="w-full dense-table">
      <thead><tr><th>Vendor</th><th className="text-right">Score</th>{!slim && <><th className="text-right">On-time</th><th className="text-right">Rejection</th></>}<th className="text-right">Orders</th></tr></thead>
      <tbody>
        {rows.map((v) => (
          <tr key={v.id}>
            <td className="text-xs"><span className="font-mono text-slate-500">{v.vendor_code}</span> <span className="font-display font-semibold">{v.name}</span></td>
            <td className="text-right tabular-nums text-xs font-display font-bold">{(v.overall_score || 0).toFixed(1)}</td>
            {!slim && (
              <>
                <td className={`text-right tabular-nums text-xs ${highlight === 'on_time_pct' ? 'text-red-600 font-bold' : ''}`}>{(v.on_time_pct || 0).toFixed(0)}%</td>
                <td className={`text-right tabular-nums text-xs ${highlight === 'quality_rejection_pct' ? 'text-red-600 font-bold' : ''}`}>{(v.quality_rejection_pct || 0).toFixed(1)}%</td>
              </>
            )}
            <td className="text-right tabular-nums text-xs">{v.total_orders}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
